// src/components/common/Header.js
import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaShoppingBasket, FaTimes, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../auth/LoginModal';
import UserProfile from '../auth/UserProfile';
import '../../styles/header.css';

const Header = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { pathname, hash } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen((open) => !open);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const activeLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const toggleLanguage = (lng) => {
    if (activeLanguage !== lng) {
      i18n.changeLanguage(lng);
    }
    closeMenu();
  };

  const openLoginModal = () => {
    closeMenu();
    setIsLoginModalOpen(true);
  };

  const navLinkClass = ({ isActive }) => `nav-link ${isActive ? 'active' : ''}`;
  const featuresLinkClass = `nav-link ${pathname === '/features' && hash !== '#how-it-works' ? 'active' : ''}`;
  const howItWorksLinkClass = `nav-link ${pathname === '/features' && hash === '#how-it-works' ? 'active' : ''}`;
  const basketLabel = activeLanguage === 'tr' ? 'Sepet' : 'Basket';

  return (
    <>
      <header className="header">
        <div className="container">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img src="/massal_ai_logo.svg" alt="Massal AI" className="logo-image" />
          </Link>

          <nav id="primary-navigation" className={`nav ${isMenuOpen ? 'nav-open' : ''}`} aria-label={t('navigation.primaryLabel')}>
            <NavLink to="/" className={navLinkClass} onClick={closeMenu}>
              {t('navigation.home')}
            </NavLink>
            <Link to="/features" className={featuresLinkClass} onClick={closeMenu}>
              {t('navigation.features')}
            </Link>
            <Link to="/features#how-it-works" className={howItWorksLinkClass} onClick={closeMenu}>
              {t('navigation.howItWorks')}
            </Link>
            <NavLink to="/examples" className={navLinkClass} onClick={closeMenu}>
              {t('navigation.examples')}
            </NavLink>

            <div className="mobile-nav-actions" aria-label="Header actions">
              <NavLink to="/contact" className={({ isActive }) => `header-action-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
                Kurumlar
              </NavLink>
              <span className="mobile-basket-visual" aria-label={basketLabel} title={basketLabel}>
                <FaShoppingBasket />
                <span>{basketLabel}</span>
              </span>
              <NavLink to="/create-story" className="header-create-button mobile-create-button" onClick={closeMenu}>
                Kitap Oluştur
              </NavLink>
            </div>
          </nav>

          <div className="header-actions">
            <div className="language-toggle" role="group" aria-label={t('language.selectorLabel')}>
              {['en', 'tr'].map((lng) => (
                <button
                  key={lng}
                  type="button"
                  className={`language-btn ${activeLanguage === lng ? 'active' : ''}`}
                  onClick={() => toggleLanguage(lng)}
                  aria-pressed={activeLanguage === lng}
                >
                  {lng.toUpperCase()}
                </button>
              ))}
            </div>
            <NavLink to="/contact" className={({ isActive }) => `header-action-link desktop-action ${isActive ? 'active' : ''}`} onClick={closeMenu}>
              Kurumlar
            </NavLink>
            {user ? (
              <UserProfile compact />
            ) : (
              <button
                type="button"
                className="header-icon-button login-icon-button"
                onClick={openLoginModal}
                aria-label={t('auth.signIn')}
                title={t('auth.signIn')}
              >
                <FaUser />
              </button>
            )}
            <button
              type="button"
              className="header-icon-button basket-button desktop-action"
              aria-label={basketLabel}
              title={basketLabel}
              aria-disabled="true"
            >
              <FaShoppingBasket />
            </button>
            <NavLink to="/create-story" className="header-create-button desktop-action" onClick={closeMenu}>
              Kitap Oluştur
            </NavLink>
            
            <button
              type="button"
              className="menu-toggle"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? t('navigation.closeMenu') : t('navigation.openMenu')}
              aria-expanded={isMenuOpen}
              aria-controls="primary-navigation"
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </header>

      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => setIsLoginModalOpen(false)} 
      />
    </>
  );
};

export default Header;
